/**
 * Organizations Page
 *
 * Страница со списком организаций.
 * Использует серверную фильтрацию, поиск и сортировку.
 * DataTable работает в серверном режиме (skipProcessing).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Title,
  Text,
  Loader,
  Center,
  Stack,
  Box,
  Group,
  Pagination,
  Select,
  Button
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconBuilding, IconHome } from '@tabler/icons-react';
import { OrganizationsTable } from '../components/Organizations/OrganizationsTable';
import { CreateCompanyGroupModal } from '../components/Organizations/CreateCompanyGroupModal';
import { CreateIndividualOrgModal } from '../components/Organizations/CreateIndividualOrgModal';
import tokens from '../theme/designTokens';
import { useSessionState } from '../hooks/useSessionState';

// Дефолтные фильтры (пустые Set = показать все)
const DEFAULT_FILTERS = {
  category: new Set(),
  organization_type: new Set(),
  district_name: new Set(),
  company_group_name: new Set(),
  cooperation_stage: new Set(),
  assigned_to_user_id: new Set(),
  manager_user_id: new Set(),
  partnership_status: new Set(),
};

// Сериализация/десериализация фильтров (Set ↔ Array)
const filtersSerialize = (filters) => {
  const obj = {};
  Object.entries(filters).forEach(([k, v]) => {
    obj[k] = v instanceof Set ? [...v] : [];
  });
  return obj;
};

const filtersDeserialize = (raw) => {
  const obj = {};
  // Восстанавливаем только известные ключи фильтров
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    obj[k] = new Set(Array.isArray(raw[k]) ? raw[k] : []);
  });
  return obj;
};

const Organizations = () => {
  // Data state (не персистим — загружаются при каждом mount)
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Company Group Modal state
  const [companyGroupModalOpened, setCompanyGroupModalOpened] = useState(false);
  const [individualOrgModalOpened, setIndividualOrgModalOpened] = useState(false);

  // Статусы партнёрства (из API)
  const [statusesMap, setStatusesMap] = useState(null);

  // Опции для фильтров (из /api/organizations/filter-options)
  const [filterOptions, setFilterOptions] = useState(null);

  // Pagination state (персистим page и pageSize)
  const [page, setPage] = useSessionState('orgs:page', 1);
  const [pageSize, setPageSize] = useSessionState('orgs:pageSize', 50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Серверный поиск (с debounce, персистим searchValue)
  const [searchValue, setSearchValue] = useSessionState('orgs:search', '');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Серверная сортировка (персистим)
  const [sortBy, setSortBy] = useSessionState('orgs:sortBy', null);
  const [sortOrder, setSortOrder] = useSessionState('orgs:sortOrder', 'asc');

  // Filters state (Excel-style column filters, персистим)
  const filtersOptions = useMemo(() => ({
    serialize: filtersSerialize,
    deserialize: filtersDeserialize,
  }), []);
  const [filters, setFilters] = useSessionState('orgs:filters', DEFAULT_FILTERS, filtersOptions);

  // AbortController для отмены предыдущих запросов
  const abortControllerRef = useRef(null);

  // Загрузка статусов партнёрства
  const loadStatuses = useCallback(async () => {
    try {
      const response = await fetch('/api/partnership-statuses', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        data.forEach(s => {
          map[s.code] = { color: s.color, label: s.name };
        });
        setStatusesMap(map);
      }
    } catch (err) {
      console.error('Failed to load partnership statuses:', err);
    }
  }, []);

  // Загрузка опций для фильтров (один раз при монтировании)
  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await fetch('/api/organizations/filter-options', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions({
          categories: data.categories,
          districtNames: data.district_names,
          companyGroupNames: data.company_group_names,
          salesUsers: data.sales_users,
          managerUsers: data.manager_users,
        });
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
    loadFilterOptions();
  }, [loadStatuses, loadFilterOptions]);

  /**
   * Конвертация Set-фильтров в query-параметры для API
   */
  const buildFilterParams = useCallback((params) => {
    // Маппинг ключей фильтров → параметры API
    const paramNameMap = {
      partnership_status: 'partnership_statuses',
      assigned_to_user_id: 'assigned_to_user_ids',
      manager_user_id: 'manager_user_ids',
    };

    // Маппинг ключей → include_empty_* параметры
    const emptyParamMap = {
      category: 'include_empty_category',
      district_name: 'include_empty_district',
      company_group_name: 'include_empty_company_group',
      assigned_to_user_id: 'include_unassigned_sales',
      manager_user_id: 'include_unassigned_manager',
    };

    Object.entries(filters).forEach(([key, selectedValues]) => {
      if (!selectedValues || selectedValues.size === 0) return; // Пустой Set = показать все
      if (selectedValues.has('__none__')) return; // Обрабатывается отдельно

      const hasEmpty = selectedValues.has('__empty__');
      const actualValues = [...selectedValues].filter(v => v !== '__empty__');

      const apiParamName = paramNameMap[key] || key;

      // Передаём каждое значение отдельным query param (FastAPI принимает как List)
      actualValues.forEach(v => params.append(apiParamName, v));

      // Флаг для включения пустых значений
      if (hasEmpty && emptyParamMap[key]) {
        params.append(emptyParamMap[key], 'true');
      }
    });
  }, [filters]);

  // Fetch organizations from API
  const fetchOrganizations = useCallback(async () => {
    // Если хоть один фильтр = __none__, результат гарантированно пуст
    const hasNoneFilter = Object.values(filters).some(f => f && f.has('__none__'));
    if (hasNoneFilter) {
      setOrganizations([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    // Отменяем предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      // Поиск
      if (debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }

      // Сортировка
      if (sortBy) {
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
      }

      // Фильтры колонок
      buildFilterParams(params);

      const response = await fetch(`/api/organizations?${params}`, {
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('Не удалось загрузить организации');
      }

      const data = await response.json();
      setOrganizations(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      if (err.name === 'AbortError') return; // Запрос отменён — игнорируем
      console.error('Error fetching organizations:', err);
      setError(err.message);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [page, pageSize, debouncedSearch, sortBy, sortOrder, filters, buildFilterParams]);

  // Fetch on mount and when any parameter changes
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Сброс на страницу 1 при изменении фильтров/поиска/сортировки
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  // Handle page size change
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Организации
            </Title>
            <Text c="dimmed" size="sm">
              Всего: {total}
            </Text>
          </Box>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconHome size={16} />}
              onClick={() => setIndividualOrgModalOpened(true)}
            >
              Инд. занятия
            </Button>
            <Button
              leftSection={<IconBuilding size={16} />}
              onClick={() => setCompanyGroupModalOpened(true)}
            >
              Создать группу компаний
            </Button>
          </Group>
        </Group>

        {/* Table */}
        {error ? (
          <Center p="xl">
            <Text c="red">{error}</Text>
          </Center>
        ) : (
          <>
            <OrganizationsTable
              data={organizations}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              statusesMap={statusesMap}
              filterOptions={filterOptions}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              searchValue={searchValue}
            />

            {/* Pagination */}
            <Group justify="space-between" mt="md">
              <Group gap="xs">
                <Text size="sm" c="dimmed">Показывать:</Text>
                <Select
                  value={pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={[
                    { value: '25', label: '25' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' }
                  ]}
                  size="xs"
                  style={{ width: 80 }}
                />
              </Group>

              {totalPages > 1 && (
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                  siblings={1}
                  boundaries={1}
                />
              )}

              <Text size="sm" c="dimmed">
                Страница {page} из {totalPages}
              </Text>
            </Group>

            {/* Индикатор загрузки при обновлении (не первый раз) */}
            {loading && organizations.length > 0 && (
              <Center>
                <Loader size="sm" />
              </Center>
            )}
          </>
        )}

        {/* Первоначальная загрузка */}
        {loading && organizations.length === 0 && !error && (
          <Center p="xl">
            <Loader size="lg" />
          </Center>
        )}
      </Stack>

      {/* Create Company Group Modal */}
      <CreateCompanyGroupModal
        opened={companyGroupModalOpened}
        onClose={() => setCompanyGroupModalOpened(false)}
        onSuccess={() => {
          fetchOrganizations();
          loadFilterOptions();
        }}
      />

      {/* Create Individual Organization Modal */}
      <CreateIndividualOrgModal
        opened={individualOrgModalOpened}
        onClose={() => setIndividualOrgModalOpened(false)}
        onSuccess={(created) => {
          fetchOrganizations();
          loadFilterOptions();
        }}
      />
    </Box>
  );
};

export default Organizations;
